// Library.jsx

import { useEffect, useMemo, useState } from "react";
import { studentDetailsLibraryReport } from "../../../../api/StudentDetails";
import { SweetAlertShow } from "../../../master/Master";

/* ---------- helpers ---------- */

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const formatDate = (d) => {
  if (!d) return "-";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMonth = (m) => {
  if (!m) return "-";
  const [y, mo] = m.split("-");
  if (!y || !mo) return m;
  const date = new Date(Number(y), Number(mo) - 1, 1);
  if (Number.isNaN(date.getTime())) return m;
  return date.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const formatTime = (t) => {
  if (!t) return "-";
  const [h, m] = t.split(":");
  if (h === undefined) return t;
  const hour = Number(h);
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m} ${suffix}`;
};

const getFeeStatus = (due, paid) => {
  if (due <= 0) return { label: "Paid", color: "success" };
  if (paid > 0) return { label: "Partial", color: "warning" };
  return { label: "Due", color: "danger" };
};

/* ---------- skeleton / empty states ---------- */

function SummarySkeleton() {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <div className="col-lg-3 col-md-6" key={i}>
          <div className="card shadow-sm border-0">
            <div className="card-body text-center">
              <span className="placeholder-glow d-block mb-2">
                <span
                  className="placeholder rounded-circle"
                  style={{ width: 32, height: 32, display: "inline-block" }}
                ></span>
              </span>
              <span className="placeholder-glow d-block mb-1">
                <span className="placeholder col-6"></span>
              </span>
              <span className="placeholder-glow d-block">
                <span className="placeholder col-4"></span>
              </span>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function InfoCardSkeleton() {
  return (
    <div className="col-lg-6">
      <div className="card shadow-sm h-100 border-0">
        <div className="card-header bg-white">
          <span className="placeholder-glow d-block">
            <span className="placeholder col-4"></span>
          </span>
        </div>
        <div className="card-body">
          {[0, 1, 2, 3, 4].map((i) => (
            <span className="placeholder-glow d-block mb-2" key={i}>
              <span
                className="placeholder col-12"
                style={{ height: 16 }}
              ></span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="col-12">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white">
          <span className="placeholder-glow d-block">
            <span className="placeholder col-3"></span>
          </span>
        </div>
        <div className="card-body">
          {[0, 1, 2].map((i) => (
            <span className="placeholder-glow d-block mb-2" key={i}>
              <span
                className="placeholder col-12"
                style={{ height: 18 }}
              ></span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="col-12">
      <div className="card shadow-sm border-0">
        <div className="card-body text-center py-5">
          <i className={`${icon} text-muted`} style={{ fontSize: 42 }}></i>
          <h6 className="mt-3 mb-1">{title}</h6>
          <p className="text-muted small mb-0">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

/* ---------- main component ---------- */

export default function Library({ id }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const getStudentDetailsMain = async (student_id) => {
    setLoading(true);
    try {
      const response = await studentDetailsLibraryReport(student_id);
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentDetailsMain(id);
  }, [id]);

  const info = data?.student_library_info ?? null;
  const feeMonths = data?.student_library_n_fees ?? [];

  const totalDue = useMemo(
    () => feeMonths.reduce((sum, m) => sum + (toNum(m.left_amount) ?? 0), 0),
    [feeMonths],
  );

  const totalPaid = useMemo(
    () => feeMonths.reduce((sum, m) => sum + (toNum(m.paid_amount) ?? 0), 0),
    [feeMonths],
  );

  return (
    <div className="row g-3">
      {/* Summary Cards */}
      {loading ? (
        <SummarySkeleton />
      ) : info ? (
        <>
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="ri-book-open-line fs-2 text-primary"></i>
                <h5 className="mt-2 mb-0">
                  {info.status === "1" ? "Active" : "Inactive"}
                </h5>
                <small className="text-muted">Membership</small>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="ri-armchair-line fs-2 text-success"></i>
                <h5 className="mt-2 mb-0">{info.seat?.seat_no ?? "-"}</h5>
                <small className="text-muted">Seat No.</small>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="ri-time-line fs-2 text-warning"></i>
                <h5 className="mt-2 mb-0 text-capitalize">
                  {info.shift?.name?.toLowerCase() ?? "-"}
                </h5>
                <small className="text-muted">Shift</small>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i
                  className={`ri-wallet-3-line fs-2 text-${totalDue > 0 ? "danger" : "success"}`}
                ></i>
                <h5
                  className={`mt-2 mb-0 text-${totalDue > 0 ? "danger" : "success"}`}
                >
                  ₹{totalDue}
                </h5>
                <small className="text-muted">Due Fee</small>
              </div>
            </div>
          </div>
        </>
      ) : (
        <EmptyState
          icon="ri-book-open-line"
          title="No library membership found"
          subtitle="This student isn't enrolled with a library yet."
        />
      )}

      {/* Library Information */}
      {loading ? (
        <InfoCardSkeleton />
      ) : info ? (
        <div className="col-lg-6">
          <div className="card shadow-sm h-100 border-0">
            <div className="card-header bg-white">
              <h6 className="mb-0">
                <i className="ri-book-open-line text-primary me-2"></i>
                Library Information
              </h6>
            </div>
            <div className="card-body">
              <table className="table table-borderless table-sm mb-0">
                <tbody>
                  <tr>
                    <th width="40%">Library</th>
                    <td>{info.library?.name ?? "-"}</td>
                  </tr>
                  <tr>
                    <th>Library Address</th>
                    <td>{info.library?.address ?? "-"}</td>
                  </tr>
                  <tr>
                    <th>Seat No.</th>
                    <td>{info.seat?.seat_no ?? "-"}</td>
                  </tr>
                  <tr>
                    <th>Shift</th>
                    <td className="text-capitalize">
                      {info.shift?.name?.toLowerCase() ?? "-"}
                      {info.shift?.start_time ? (
                        <span className="text-muted">
                          {" "}
                          ({formatTime(info.shift.start_time)} –{" "}
                          {formatTime(info.shift.end_time)})
                        </span>
                      ) : null}
                    </td>
                  </tr>
                  <tr>
                    <th>Fee Type</th>
                    <td>{info.fee_type ?? "-"}</td>
                  </tr>
                  <tr>
                    <th>Joining Date</th>
                    <td>{formatDate(info.joining_date)}</td>
                  </tr>
                  <tr>
                    <th>Expiry / Last Date</th>
                    <td>{info.last_date ? formatDate(info.last_date) : "-"}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      <span
                        className={`badge bg-${info.status === "1" ? "success" : "secondary"}-subtle text-${info.status === "1" ? "success" : "secondary"}`}
                      >
                        <i
                          className={`ri-${info.status === "1" ? "checkbox-circle" : "close-circle"}-line me-1`}
                        ></i>
                        {info.status === "1" ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {/* Fee Summary */}
      {loading ? (
        <InfoCardSkeleton />
      ) : info ? (
        <div className="col-lg-6">
          <div className="card shadow-sm h-100 border-0">
            <div className="card-header bg-white">
              <h6 className="mb-0">
                <i className="ri-wallet-line text-success me-2"></i>
                Library Fee Summary
              </h6>
            </div>
            <div className="card-body">
              <table className="table table-borderless table-sm mb-0">
                <tbody>
                  <tr>
                    <th width="40%">Total Fee</th>
                    <td>₹{info.total_fee ?? "-"}</td>
                  </tr>
                  <tr>
                    <th>Final Fee</th>
                    <td>₹{info.final_fee ?? "-"}</td>
                  </tr>
                  <tr>
                    <th>Paid</th>
                    <td className="text-success">₹{totalPaid}</td>
                  </tr>
                  <tr>
                    <th>Due</th>
                    <td
                      className={totalDue > 0 ? "text-danger" : "text-success"}
                    >
                      ₹{totalDue}
                    </td>
                  </tr>
                  <tr>
                    <th>Seat Charge</th>
                    <td>₹{info.library?.seat_charge ?? "-"}</td>
                  </tr>
                  <tr>
                    <th>Mobile</th>
                    <td>{info.mobile ?? "-"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {/* Fee History */}
      {loading ? (
        <TableSkeleton />
      ) : feeMonths.length ? (
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white">
              <h6 className="mb-0">
                <i className="ri-history-line text-info me-2"></i>
                Library Fee History
              </h6>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Month</th>
                    <th>Total Amount</th>
                    <th>Final Amount</th>
                    <th>Paid Amount</th>
                    <th>Due</th>
                    <th>Updated</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {feeMonths
                    .slice()
                    .sort((a, b) => new Date(b.month) - new Date(a.month))
                    .map((m) => {
                      const due = toNum(m.left_amount) ?? 0;
                      const paid = toNum(m.paid_amount) ?? 0;
                      const feeStatus = getFeeStatus(due, paid);
                      return (
                        <tr key={m.id}>
                          <td>
                            <i className="ri-calendar-2-line me-1 text-muted"></i>
                            {formatMonth(m.month)}
                          </td>
                          <td>₹{m.total_amount ?? "-"}</td>
                          <td>₹{m.final_amount ?? "-"}</td>
                          <td className="text-success">₹{paid}</td>
                          <td
                            className={due > 0 ? "text-danger" : "text-success"}
                          >
                            ₹{due}
                          </td>
                          <td>{formatDate(m.updated_at)}</td>
                          <td>
                            <span
                              className={`badge bg-${feeStatus.color}-subtle text-${feeStatus.color}`}
                            >
                              {feeStatus.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <EmptyState
          icon="ri-history-line"
          title="No fee history found"
          subtitle="No library fee records have been recorded for this student yet."
        />
      )}
    </div>
  );
}
